import { useDisclosure } from '@mantine/hooks';
import clsx from 'clsx';
import { useMemo } from 'react';
import {
  DisplayType,
  FloorCeilingBarDisplay,
} from 'src/components/Listings/ComparableListings/ComparableListingItem';
import { useInventoryEventLocalContext } from 'src/components/Listings/InventoryEventPage/InventoryEventPageRework/InventoryEventLocalContext';
import { InventoryListingFilterDrawer } from 'src/components/Listings/InventoryEventPage/InventoryEventPageRework/Mobile/SortAndFilter/InventoryListingFilterDrawer';
import { InventoryListingSortingContent } from 'src/components/Listings/InventoryEventPage/InventoryEventPageRework/Mobile/SortAndFilter/InventoryListingSortingContent';
import { InventoryEventTabs } from 'src/components/Listings/InventoryEventPage/InventoryEventTabs';
import { Content } from 'src/contexts/ContentContext';
import { useMobileDrawerContext } from 'src/contexts/MobileDrawerContext';
import { useMobileListingDrawerContext } from 'src/contexts/MobileListingDrawerContext/MobileListingDrawerContext';
import {
  defaultListingSortingCriteria,
  useLocalEventMobileScrollContext,
  useMobileInventorySortAndFilterContext,
} from 'src/contexts/MobileSortAndFilterContext';
import { useMultiSelectionContext } from 'src/contexts/MultiSelectionContext';
import { vars } from 'src/core/themes';
import { BadgedIcon } from 'src/core/ui/BadgedIcon/BadgedIcon';
import {
  ActionBarItem,
  MobileFloatingActionBar,
} from 'src/core/ui/MobileFloatingActionBar/MobileFloatingActionBar';
import { MapIcon } from 'src/svgs';
import { SortIcon } from 'src/svgs/SortIcon';
import { FilterIcon } from 'src/svgs/Viagogo';
import { ContentId } from 'src/utils/constants/contentId';
import { Event, Listing, UiCurrency } from 'src/WebApiController';

import {
  PriceRangeCompListingTile,
  SingleCompListingTile,
} from './CompListingTile';
import { ListingTile } from './ListingTile';
import * as styles from './ListingTilesV2.css';
import { ListingTileItem, ListingWithComp } from './ListingTilesV2.types';
import {
  isCeilingDivider,
  isFloorDivider,
  isOwnListing,
  isPriceRangeGroup,
  isSingleCompListing,
} from './ListingTilesV2.utils';

export const ListingTilesFloatingActions = ({
  allInventory,
}: {
  allInventory: ListingWithComp[];
}) => {
  const { activeTab, setActiveTab, showMap, toggleShowMap } =
    useInventoryEventLocalContext();
  const { selectionMode } = useMultiSelectionContext();
  const { selectedListing, selectedListingGroup } =
    useMobileListingDrawerContext();
  const { hasSortingChanged, hasFilterApplied, setSortingCriteria } =
    useMobileInventorySortAndFilterContext();
  const { openWithContent, show: showMobileDrawer } = useMobileDrawerContext();
  const { isAtBottom, isAtTop } = useLocalEventMobileScrollContext();

  const [
    showListingFilter,
    { open: openListingFilter, close: closeListingFilter },
  ] = useDisclosure(false);

  const actions: ActionBarItem[] = useMemo(
    () => [
      {
        id: 'map',
        content: (
          <MapIcon
            fill={showMap ? vars.color.textBrand : vars.color.textPrimaryV2}
          />
        ),
        onClick: () => toggleShowMap(!showMap),
        className: clsx(styles.inventoryFloatingActionMap, {
          [styles.inventoryFloatingActionActive]: showMap,
        }),
      },
      {
        id: 'sort',
        content: (
          <BadgedIcon
            icon={<SortIcon fill={vars.color.textPrimaryV2} />}
            withBadge={hasSortingChanged}
          />
        ),
        className: styles.inventoryFloatingActionMap,
        onClick: () =>
          openWithContent(
            <InventoryListingSortingContent
              isAllInventory={activeTab === InventoryEventTabs.ManualPricing}
            />,
            { size: '75%' }
          ),
      },
      {
        id: 'filters',
        content: (
          <BadgedIcon
            icon={<FilterIcon fill={vars.color.textPrimaryV2} />}
            withBadge={hasFilterApplied}
          />
        ),
        className: styles.inventoryFloatingActionMap,
        onClick: () => openListingFilter(),
      },
      {
        id: 'all-listings',
        content: (
          <span
            style={{
              color:
                activeTab === InventoryEventTabs.ManualPricing
                  ? vars.color.textBrand
                  : vars.color.textPrimaryV2,
            }}
          >
            <Content id={ContentId.AllListings} />
          </span>
        ),
        type: 'button',
        onClick: () => {
          setActiveTab(
            activeTab === InventoryEventTabs.Info
              ? InventoryEventTabs.ManualPricing
              : InventoryEventTabs.Info
          );
          // Reset sorting criteria for all inventory
          if (activeTab === InventoryEventTabs.Info) {
            setSortingCriteria(defaultListingSortingCriteria);
          }
        },
        className: clsx(styles.inventoryFloatingActionMap, {
          [styles.inventoryFloatingActionActive]:
            activeTab === InventoryEventTabs.ManualPricing,
        }),
      },
    ],
    [
      showMap,
      hasSortingChanged,
      hasFilterApplied,
      activeTab,
      toggleShowMap,
      openWithContent,
      openListingFilter,
      setActiveTab,
      setSortingCriteria,
    ]
  );

  const isInInventory = useMemo(() => {
    return [InventoryEventTabs.Info, InventoryEventTabs.ManualPricing].includes(
      activeTab
    );
  }, [activeTab]);

  const showFloatingBar = useMemo(() => {
    if (!isInInventory) {
      return false;
    }
    if (
      showMobileDrawer ||
      selectionMode ||
      selectedListing ||
      selectedListingGroup ||
      showListingFilter
    ) {
      return false;
    }
    return true;
  }, [
    isInInventory,
    selectedListing,
    selectedListingGroup,
    selectionMode,
    showListingFilter,
    showMobileDrawer,
  ]);

  const hasOverlay = useMemo(() => {
    return isAtBottom && !isAtTop;
  }, [isAtBottom, isAtTop]);

  return (
    <>
      {showFloatingBar && !hasOverlay && (
        <MobileFloatingActionBar actions={actions} />
      )}
      <InventoryListingFilterDrawer
        show={showListingFilter}
        activeTab={activeTab}
        onClose={closeListingFilter}
        allInventory={allInventory}
      />
    </>
  );
};

export const InventoryListingTileWrapper = ({
  listing,
  event,
  entities,
  expanded,
  currency,
  onUpdateExpanded,
}: {
  listing: ListingTileItem;
  event: Event;
  entities: Listing[];
  expanded: boolean;
  currency?: UiCurrency;
  onUpdateExpanded: (expanded: boolean) => void;
}) => {
  if (isOwnListing(listing)) {
    const ownListing = listing.listing;
    return (
      <ListingTile
        key={ownListing.id}
        listing={ownListing}
        event={event}
        entities={entities}
      />
    );
  }

  if (isFloorDivider(listing) && currency) {
    return (
      <FloorCeilingBarDisplay
        currency={currency}
        floorCeilingPrice={listing.price}
        displayType={DisplayType.FloorDisplay}
      />
    );
  }
  if (isCeilingDivider(listing) && currency) {
    return (
      <FloorCeilingBarDisplay
        currency={currency}
        floorCeilingPrice={listing.price}
        displayType={DisplayType.CeilingDisplay}
      />
    );
  }

  if (isSingleCompListing(listing)) {
    return <SingleCompListingTile listing={listing.listing} />;
  }

  if (isPriceRangeGroup(listing)) {
    return (
      <PriceRangeCompListingTile
        listingGroup={listing}
        expanded={expanded}
        onUpdateExpanded={onUpdateExpanded}
      />
    );
  }

  return null;
};
